/**
 * Blocked types: the ones being refused at the door.
 *
 * This used to sit under Needs review, which meant it disappeared entirely
 * once that queue was empty. Blocking is a decision people revisit rarely and
 * at exactly the wrong moment (a sender starts erroring and nobody remembers
 * why), so it gets a stable home of its own.
 */
import { Ban } from 'lucide-react';
import { Button, Card } from '@servicecompressionllc/sc-design-system';
import type { TenantEventType } from '../../lib/types.js';
import { formatAge } from './EventsPage.js';

interface Props {
  blocked: TenantEventType[];
  /** Re-registering is the way back: it reopens the drawer on this row. */
  onUnblock: (entry: TenantEventType) => void;
}

export const EventBlockedTab = ({ blocked, onUnblock }: Props) => {
  if (blocked.length === 0) {
    return (
      <Card className="px-6 py-12 text-center">
        <p className="text-ink text-sm font-semibold">Nothing is blocked</p>
        <p className="text-ink-muted mx-auto mt-2 max-w-[56ch] text-sm">
          Blocking a type refuses it at the door, so its sender starts getting errors. Anything you
          block shows up here, and you can undo it at any time.
        </p>
      </Card>
    );
  }

  return (
    <div>
      <p className="text-ink-muted max-w-[74ch] text-sm">
        These are refused at the door. The sender gets an error, which is the point: someone needs
        to know to stop sending it. Re-register one to start accepting it again.
      </p>

      <Card padding="none" className="mt-4">
        {blocked.map((entry) => (
          <div
            key={entry.id}
            className="border-outline flex items-center gap-4 border-b px-4 py-3 last:border-b-0"
          >
            <Ban className="text-danger h-4 w-4 flex-shrink-0" />
            <div className="min-w-0 flex-grow">
              <div className="text-ink font-mono text-sm">{entry.type}</div>
              <div className="text-ink-muted mt-0.5 text-xs">
                {entry.receivedCount.toLocaleString()} received before it was blocked
                {entry.lastSeenAt ? ` · last seen ${formatAge(entry.lastSeenAt)} ago` : ''}
                {entry.lastSenderName ? ` · from ${entry.lastSenderName}` : ''}
              </div>
            </div>
            <Button variant="secondary" onClick={() => onUnblock(entry)}>
              Re-register
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
};
